import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { finalize, skip } from 'rxjs/operators';
import { Borrower } from '../../../models/borrower-model';
import { AbadConsentStep } from '../../../models/wizard/abad-consent-step-model';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import { FeatureFlags } from '../../../models/feature-flags.model';
import { localizeField } from '../../../transloco/localize-field';
import { ErrorMessage } from '../../../models/error-message.model';
import { getErrorMessageOrDefault } from 'projects/shared/utils/error.utils';

@Component({
    selector: 'abad-consent-step',
    templateUrl: 'abad-consent-step.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AbadConsentStepComponent extends WizardStepComponentBase<AbadConsentStep> implements OnInit, OnDestroy {

  showAbadVerbiage: boolean = false;
  abadText: string | undefined = '';

  mainBorrower!: Borrower;

  abadAcknowledged: boolean = false;

  private _abadGenerationEnabled: boolean = false;
  private _config: FeatureFlags | undefined;
  private _langSub?: Subscription;

  get canMoveToNextStep(): boolean {
    return this.abadAcknowledged;
  }

  constructor(private readonly _injector: Injector,
    private readonly _cdr: ChangeDetectorRef) {
    super(_injector);
    this.saveMortgageApplicationBeforeNextStep = true;
    if (this.currentBorrower) {
      this.mainBorrower = this.currentBorrower;
      this.abadAcknowledged = this.mainBorrower.hasAcknowledgedAbad || false;
    }
  }

  ngOnInit() {
    super.ngOnInit();
    this.startSpinner();
    this.mortgageApplicationService.getConfig(this.mortgageApplication.applicationId).pipe(
      finalize(() => {
        this.stopSpinner();
        this._cdr.markForCheck();
      })
    )
      .subscribe(response => {
        this._config = response;
        this.refreshAbadText();
        this.showAbadVerbiage = response.showAbadVerbiage || false;
        this._abadGenerationEnabled = response.abadGenerationEnabled || false;
        this._cdr.markForCheck();
        this.skipStepIfNotApplicable();
      });
    this._langSub = this.translocoService.langChanges$.pipe(skip(1)).subscribe(() => {
      this.refreshAbadText();
      this._cdr.markForCheck();
    });
  }

  private skipStepIfNotApplicable(): void {
    const nothingToAcknowledge = !this.showAbadVerbiage && !this._abadGenerationEnabled;
    if (this.inCoApplyFlow || nothingToAcknowledge || this.mainBorrower?.hasAcknowledgedAbad) {
      if (this.cameHereWithBackNavigation) {
        this.wizardFlowService.navigateBackward();
      } else {
        this.wizardFlowService.navigateForward();
      }
    }
  }

  private refreshAbadText(): void {
    this.abadText = localizeField(this._config, 'abadText', this.translocoService.getActiveLang());
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._langSub?.unsubscribe();
  }

  onNextClicked() {
    if (!this.abadAcknowledged || !this.mainBorrower?.contactId || this.mainBorrower.hasAcknowledgedAbad) {
      super.onNextClicked();
      return;
    }

    this.startSpinner();
    this.mortgageApplicationService.generateAbadDocument(this.mortgageApplication.applicationId,
      { abadText: this.abadText, borrowerId: this.mainBorrower.contactId })
      .subscribe({
        next: () => {
          this.mainBorrower.hasAcknowledgedAbad = true;
          this.stopSpinner();
          super.onNextClicked();
        },
        error: (error: unknown) => {
          this.stopSpinner();
          this.showError(new ErrorMessage(
            this.translocoService.translate('wizard.consent.errorRecordingConsentTitle'),
            getErrorMessageOrDefault(error, {
              defaultMessage: this.translocoService.translate('wizard.consent.errorRecordingConsentMessage')
            })));
          this._cdr.markForCheck();
          window.scroll(0, 0);
        }
      });
  }
}
